import { Briefcase, Plus, X } from "@phosphor-icons/react"
import { Button } from "@tokenterminal/core/ui/button/Button"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import { Combobox } from "@tokenterminal/core/ui/samsCombobox/Combobox"
import { colorsV4 } from "@tokenterminal/ui/theme/themeV4/colorsContract.css"
import Image from "next/image"
import {
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useMemo,
} from "react"
import { useAvailableProjectSlugsForMetric } from "../hooks/useAvailableProjectSlugsForMetric"
import { type ProjectOverview, useProjectsList } from "../hooks/useProjectsList"
import { type Comparison } from "../MetricsTab/ComparisonToolbar"

const ComparisonButton = ({
  comparison,
  setComparison,
  metric,
  isDisabled,
  marketSector,
  data_id,
  isEcosystemMetric,
  trigger = (
    <Button
      variant="ghost"
      isDisabled={isDisabled}
      size="small"
      style={{
        border: "1px dashed",
        borderColor: colorsV4.border.default,
      }}
    >
      Add comparison
      <Icon as={Plus} />
    </Button>
  ),
}: {
  comparison: Comparison | null
  metric: string
  isDisabled?: boolean
  setComparison: Dispatch<SetStateAction<Comparison | null>>
  marketSector: {
    id: string
    name: string
  }
  data_id: string
  isEcosystemMetric?: boolean
  trigger?: ReactNode
}) => {
  const { data: projectIdsThatHaveMetric1 = [] } =
    useAvailableProjectSlugsForMetric(metric.replace(/_/g, "-"))

  const { data: projectsInMarketSector = [] } = useProjectsList({
    marketSectors: [marketSector.id],
  })

  const suggestedComparisonProjects = projectsInMarketSector
    .filter((project) => projectIdsThatHaveMetric1.includes(project.slug))
    .filter((project) => project.data_id !== data_id)
    .slice(0, 3)

  const { data: projectIdsThatHaveMetric = [] } =
    useAvailableProjectSlugsForMetric(metric.replace(/_/g, "-"))

  // Fetch all projects so we can get the labels and logos
  const { data: allProjects = [] } = useProjectsList()

  const projectsThatHaveMetric = useMemo(() => {
    return allProjects.filter((project) =>
      projectIdsThatHaveMetric.includes(project.slug)
    )
  }, [allProjects, projectIdsThatHaveMetric])

  const sortedItems = useMemo<ProjectOverview[]>(
    () =>
      projectsThatHaveMetric
        .filter((project) => project.data_id !== data_id)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [projectsThatHaveMetric, data_id]
  )

  const handleSelect = (id: string | null) => {
    if (id === "market_sector") {
      setComparison({
        type: "market_sector",
        id: marketSector.id,
        label: marketSector.name,
      })
      return
    }

    let modifiedId = id
    if (id?.startsWith("suggested-")) {
      modifiedId = id.replace("suggested-", "")
    }

    const project = projectsThatHaveMetric.find(
      (project) => project.data_id === modifiedId
    )

    setComparison({
      type: "project",
      data_id: project?.data_id || "",
      project_id: project?.project_id || "",
      label: project?.name || "",
      logo: project?.logo || "",
    })
  }

  const items = useMemo(() => {
    return [
      ...(!isEcosystemMetric
        ? [
            <Combobox.Item
              key="market_sector"
              id="market_sector"
              value={marketSector.name}
            >
              <div className="flex items-center gap-2">
                <Icon as={Briefcase} />
                {marketSector.name}
              </div>
            </Combobox.Item>,
            ...suggestedComparisonProjects.map((project) => (
              <Combobox.Item
                key={"suggested-" + project.data_id}
                id={"suggested-" + project.data_id}
                value={project.name}
              >
                <div className="flex items-center gap-2">
                  <Image
                    src={`https://static1.tokenterminal.com/${project.logo}`}
                    width={16}
                    height={16}
                    className="rounded-full overflow-hidden"
                    alt={project.name}
                  />
                  {project.name}
                </div>
              </Combobox.Item>
            )),
            <Combobox.Divider key="divider" />,
          ]
        : []),
      ...sortedItems.map((project) => (
        <Combobox.Item
          key={project.data_id}
          id={project.data_id}
          value={project.name}
        >
          <div className="flex items-center gap-2">
            <Image
              src={`https://static1.tokenterminal.com/${project.logo}`}
              width={16}
              height={16}
              className="rounded-full overflow-hidden"
              alt={project.name}
            />
            {project.name}
          </div>
        </Combobox.Item>
      )),
    ]
  }, [
    isEcosystemMetric,
    marketSector.name,
    sortedItems,
    suggestedComparisonProjects,
  ])

  if (!!comparison) {
    return (
      <Button
        // variant="solid"
        isDisabled={isDisabled}
        onPress={() => setComparison(null)}
        size="small"
      >
        {comparison.type === "market_sector" ? (
          <Icon as={Briefcase} />
        ) : (
          <Image
            src={`https://static1.tokenterminal.com/${comparison.logo}`}
            width={16}
            height={16}
            className="rounded-full overflow-hidden"
            alt={comparison.label}
          />
        )}
        {comparison.label}
        <Icon as={X} />
      </Button>
    )
  }

  return (
    <Combobox
      drawerHeaderTitle="Select a comparison"
      selectionMode="single"
      onChangeSelectedId={handleSelect}
      virtualized
      trigger={trigger}
    >
      {items.length > 0 ? (
        items
      ) : (
        <Combobox.EmptyState>No projects found</Combobox.EmptyState>
      )}
    </Combobox>
  )
}

export default ComparisonButton
